import { useEffect, useMemo } from 'react';
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { mondayOf } from '@jisy/shared';
import type {
  AdfDocument,
  Attachment,
  FathomConnection,
  FieldCandidates,
  Member,
  Comment,
  IssueChildren,
  IssueConvert,
  IssueCreate,
  IssueCreateOptions,
  IssueDetail,
  IssueFieldOptions,
  IssueRow,
  IssueSummary,
  IssueUpdate,
  IssueTypeRef,
  Locale,
  Me,
  MeetingPage,
  Note,
  NoteBody,
  NoteImage,
  NoteList,
  NotificationList,
  NotificationsRead,
  PlanView,
  PriorityRef,
  Project,
  ProjectCreate,
  ProjectLink,
  ProjectLinkList,
  StatusRef,
  SyncStatus,
  TableSettings,
  TenantSettings,
  Transition,
  TransitionRequest,
  UserRef,
  AvailableSites,
  WorkspaceDisconnectResult,
  WorkspaceList,
} from '@jisy/shared';
import { apiRequest, apiUpload } from './client.js';
import { todayIso } from '../lib/format.js';
import { setLocale } from '../i18n/index.js';

/**
 * Новый раздел — новый проект в Jira. Список разделов перечитывается запросом, а не правится
 * на месте: цвет метки раздела зависит от позиции в списке, и добавление строки в кэш
 * перекрасило бы соседей не так, как это сделает сервер.
 */
export function useProjectCreate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ProjectCreate) =>
      apiRequest<Project>('/api/projects', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Фильтры считаются по задачам раздела: новый раздел меняет их состав.
      void queryClient.invalidateQueries({ queryKey: ['filters'] });
    },
  });
}

/** Состояние кэша: показывается подписью «Синхронизировано N минут назад». */
export interface Freshness {
  fetchedAt: string | null;
  stale: boolean;
}

export interface ProjectsResponse {
  projects: Project[];
  total: number;
  freshness: Freshness;
  lastSyncAt: string | null;
}

export interface IssuesResponse {
  /** Строки таблицы; в режиме дерева подзадачи едут в `children` своего родителя. */
  items: IssueRow[];
  nextCursor: string | null;
  total: number | null;
  freshness: Freshness;
}

export interface FiltersResponse {
  statuses: StatusRef[];
  /** Признака подзадачи в кэше задач нет — фильтру он и не нужен. */
  issueTypes: Array<Pick<IssueTypeRef, 'id' | 'name' | 'iconUrl'>>;
  priorities: PriorityRef[];
  assignees: Array<Pick<UserRef, 'accountId' | 'displayName' | 'avatarUrl'>>;
}

export interface AccountResponse {
  me: Me;
  settings: TenantSettings;
  /** Личные настройки таблицы: ширины, порядок и видимость колонок, показ завершённых. */
  tableSettings: TableSettings;
}

/** Параметры таблицы, влияющие на запрос. */
export interface IssuesFilter {
  projectId?: string | undefined;
  /** Отбор по нескольким разделам: раздел как фильтр, а не как режим таблицы. */
  projectIds?: string[];
  q?: string | undefined;
  status?: string[];
  assignee?: string[];
  unassigned?: boolean;
  type?: string[];
  priority?: string[];
  doneOnly?: boolean;
  /** Подзадачи под родителями — таблице; окну подбора в план нужен плоский список. */
  tree?: boolean;
  /** Только задачи этого уровня иерархии: выбор родителя для подзадачи. */
  level?: number;
  sort: string;
  dir: 'asc' | 'desc';
}

export const accountQuery = queryOptions({
  queryKey: ['account'],
  queryFn: () => apiRequest<AccountResponse>('/api/me'),
  // Профиль почти не меняется, но при отзыве доступа его стоит перечитать.
  staleTime: 60_000,
  retry: false,
});

export const projectsQuery = queryOptions({
  queryKey: ['projects'],
  queryFn: () => apiRequest<ProjectsResponse>('/api/projects'),
  staleTime: 30_000,
  // Кэш наполняется фоном, поэтому список разделов подтягивается сам.
  refetchInterval: 60_000,
});

/**
 * Цвет метки раздела. Единственный источник — `colorIndex` из списка разделов: таблица и
 * планирование красят метку тем же цветом, что боковая колонка, а не считают его сами.
 * Пока список не пришёл, цвета нет: метка рисуется нейтральной и не перекрашивается потом.
 */
export function useSectionColors(): (projectKey: string) => number | null {
  const { data } = useQuery(projectsQuery);

  return useMemo(() => {
    const byKey = new Map<string, number>(
      (data?.projects ?? []).map((project) => [project.key, project.colorIndex]),
    );
    return (projectKey: string) => byKey.get(projectKey) ?? null;
  }, [data]);
}

/**
 * Значения фильтров считаются по задачам выбранного раздела, поэтому раздел входит в ключ:
 * переключение возвращает уже готовый список из кэша, а не заставляет ждать запрос.
 */
export function filtersQuery(projectId: string | null) {
  return queryOptions({
    queryKey: ['filters', projectId],
    queryFn: () =>
      apiRequest<FiltersResponse>('/api/meta/filters', {
        query: { projectId: projectId ?? undefined },
      }),
    staleTime: 5 * 60_000,
  });
}

/**
 * Ход синхронизации. Пока она идёт, состояние перечитывается каждые две секунды: первая
 * загрузка тенанта занимает минуты, и пользователю нужно видеть, что работа движется.
 *
 * Опрос продолжается и на признаке первой загрузки, хотя очередь уже пуста. Экран загрузки
 * показывается по обоим признакам, а `refetchOnWindowFocus` в приложении выключен: остановись
 * опрос — вкладка осталась бы с этим экраном до перезагрузки страницы, даже когда сервер давно
 * отвечает иначе.
 */
export const syncStatusQuery = queryOptions({
  queryKey: ['sync-status'],
  queryFn: () => apiRequest<SyncStatus>('/api/sync/status'),
  refetchInterval: (query) => {
    const status = query.state.data;
    return status?.running === true || status?.firstRun === true ? 2_000 : false;
  },
  staleTime: 1_000,
});

/** Кнопка «Обновить» в списке разделов. */
export function useSyncNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiRequest<SyncStatus>('/api/sync', { method: 'POST' }),
    onSuccess: (status) => {
      queryClient.setQueryData(['sync-status'], status);
    },
  });
}

const PAGE_SIZE = 50;

export function issuesQuery(filter: IssuesFilter) {
  return infiniteQueryOptions({
    queryKey: ['issues', filter],
    queryFn: ({ pageParam, signal }) =>
      apiRequest<IssuesResponse>('/api/issues', {
        signal,
        query: {
          projectId: filter.projectId,
          projectIds: filter.projectIds,
          q: filter.q?.trim() || undefined,
          status: filter.status,
          assignee: filter.assignee,
          unassigned: filter.unassigned ? 'true' : undefined,
          type: filter.type,
          priority: filter.priority,
          // Завершённые сервер прячет сам: параметр отправляется, только когда нужны они одни.
          doneOnly: filter.doneOnly ? 'true' : undefined,
          tree: filter.tree ? 'true' : undefined,
          level: filter.level,
          sort: filter.sort,
          dir: filter.dir,
          limit: PAGE_SIZE,
          cursor: pageParam ?? undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 15_000,
  });
}

/** Смена языка: сохраняется на сервере и применяется сразу, не дожидаясь ответа. */
export function useLocaleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locale: Locale) =>
      apiRequest<{ ok: true }>('/api/me/locale', { method: 'PUT', body: { locale } }),
    onMutate: (locale) => {
      setLocale(locale);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['account'] });
    },
  });
}

/**
 * Сохранение настроек таблицы. Ответ сервера в кэш не пишем: пока запрос летит, пользователь
 * успевает потянуть колонку дальше, и ответ вернул бы её на прежнее место.
 */
export function useTableSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: TableSettings) =>
      apiRequest<{ tableSettings: TableSettings }>('/api/me/table-settings', {
        method: 'PUT',
        body: settings,
      }),
    onError: () => {
      // Не сохранилось — показываем то, что действительно лежит на сервере.
      void queryClient.invalidateQueries({ queryKey: ['account'] });
    },
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiRequest<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.clear();
      window.location.assign('/connect');
    },
  });
}

// ── Уведомления ──────────────────────────────────────────────────────────────

/**
 * Непрочитанное. Запрос уходит в Jira, а не в кэш Jisy, поэтому опрос редкий: колокольчик —
 * не биржевой терминал, две минуты задержки в нём никого не подводят. При возвращении на
 * вкладку список перечитывается сам, этим занимается react-query.
 */
export const notificationsQuery = queryOptions({
  queryKey: ['notifications'],
  queryFn: () => apiRequest<NotificationList>('/api/notifications'),
  refetchInterval: 120_000,
  staleTime: 60_000,
  // Недоступная Jira не должна превращаться в очередь повторов на каждый тик опроса.
  retry: false,
});

/**
 * Отметка о прочтении. Список правится сразу, не дожидаясь ответа: счётчик на колокольчике
 * обязан гаснуть в момент нажатия, иначе кажется, что кнопка не сработала.
 */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: NotificationsRead) =>
      apiRequest<{ ok: true }>('/api/notifications/read', { method: 'POST', body: input }),
    onMutate: (input) => {
      queryClient.setQueryData<NotificationList>(['notifications'], (previous) => {
        if (!previous) return previous;
        if (input.all === true) return { items: [], unread: 0 };

        const keys = new Set(input.issueKeys ?? []);
        const items = previous.items.filter((item) => !keys.has(item.issueKey));
        // Счётчик мог быть больше списка (упёрлись в потолок) — уменьшаем на столько же.
        return { items, unread: Math.max(0, previous.unread - (previous.items.length - items.length)) };
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Сколько непрочитанных комментариев в каждой задаче — метка в строке таблицы. Считается по
 * тому же списку, что показывает колокольчик: отдельного запроса ради счётчика нет, а значит
 * метка и список уведомлений не могут разойтись между собой.
 */
export function useUnreadCommentCounts(): ReadonlyMap<string, number> {
  const { data } = useQuery(notificationsQuery);

  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of data?.items ?? []) {
      // Назначение задачи — не сообщение, в счётчике комментариев ему не место.
      if (item.kind !== 'comment') continue;
      counts.set(item.issueKey, (counts.get(item.issueKey) ?? 0) + 1);
    }
    return counts;
  }, [data]);
}

/**
 * Открытая карточка гасит уведомления своей задачи: комментарии в ней человек уже увидел.
 * Запрос уходит, только если гасить действительно есть что, — иначе каждое открытие задачи
 * тянуло бы за собой лишний поход в Jira за обновлённым списком.
 */
export function useMarkIssueNotificationsRead(issueKey: string): void {
  const queryClient = useQueryClient();
  const { mutate } = useMarkNotificationsRead();

  useEffect(() => {
    const list = queryClient.getQueryData<NotificationList>(['notifications']);
    if (!list?.items.some((item) => item.issueKey === issueKey)) return;
    mutate({ issueKeys: [issueKey] });
  }, [issueKey, mutate, queryClient]);
}

// ── Модальное окно задачи ────────────────────────────────────────────────────

export function issueQuery(issueKey: string | null) {
  return queryOptions({
    queryKey: ['issue', issueKey],
    queryFn: () => apiRequest<IssueDetail>(`/api/issues/${issueKey ?? ''}`),
    enabled: issueKey !== null,
    // Карточка читается прямо из Jira, поэтому держим её свежей недолго.
    staleTime: 10_000,
    retry: false,
  });
}

/**
 * Дети задачи: подзадачи у задачи, задачи у эпика. Отдельный запрос, а не поле карточки: правка
 * названия или статуса перечитывает карточку, и повторять вместе с ней поиск детей незачем.
 * У подзадачи детей не бывает — для неё запрос не уходит вовсе.
 */
export function issueChildrenQuery(issueKey: string, enabled: boolean) {
  return queryOptions({
    queryKey: ['issue-children', issueKey],
    queryFn: () => apiRequest<IssueChildren>(`/api/issues/${issueKey}/children`),
    enabled,
    staleTime: 10_000,
    retry: false,
  });
}

/**
 * Кандидаты в родители подзадачи: обычные задачи раздела по ключу или названию. Эпик и другая
 * подзадача родителем подзадачи быть не могут — их отсекает отбор по уровню на сервере.
 */
export function parentCandidatesQuery(projectId: string | null, q: string) {
  return queryOptions({
    queryKey: ['parent-candidates', projectId, q],
    queryFn: ({ signal }) =>
      apiRequest<IssuesResponse>('/api/issues', {
        signal,
        query: {
          projectId: projectId ?? undefined,
          q: q.trim() || undefined,
          level: 0,
          sort: 'updated',
          dir: 'desc',
          limit: 20,
        },
      }).then((page): IssueSummary[] => page.items),
    enabled: projectId !== null,
    // Смена запроса не должна опустошать список на время ответа.
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });
}

export function transitionsQuery(issueKey: string | null) {
  return queryOptions({
    queryKey: ['transitions', issueKey],
    queryFn: () =>
      apiRequest<{ transitions: Transition[] }>(`/api/issues/${issueKey ?? ''}/transitions`),
    enabled: issueKey !== null,
    staleTime: 30_000,
  });
}

export function commentsQuery(issueKey: string | null) {
  return queryOptions({
    queryKey: ['comments', issueKey],
    queryFn: () =>
      apiRequest<{ comments: Comment[]; total: number }>(`/api/issues/${issueKey ?? ''}/comments`),
    enabled: issueKey !== null,
    staleTime: 15_000,
  });
}

/**
 * Правка задачи. После успеха обновляем и карточку, и всё, что показывает ту же задачу:
 * таблицу, строки плана и счётчики разделов. Окно задачи открывается и из планирования —
 * без сброса плана строка осталась бы с прежним названием и статусом до перезагрузки страницы.
 */
export function useIssueUpdate(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: IssueUpdate) =>
      apiRequest<IssueDetail>(`/api/issues/${issueKey}`, { method: 'PATCH', body: input }),
    onSuccess: (issue) => {
      // Прежний родитель — до того, как ответ ляжет в кэш: смена родителя меняет оба списка.
      const before = queryClient.getQueryData<IssueDetail>(['issue', issueKey]);
      queryClient.setQueryData(['issue', issueKey], issue);
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['transitions', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['plan-view'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateParentChildren(queryClient, issue, before);
    },
  });
}

/**
 * Перенос между уровнями: задача в подзадачу и обратно. Задание в Jira идёт секунды, ответ —
 * карточка после переноса. Сбрасывается то же, что после правки, плюс списки детей прежнего
 * и нового родителя: задача ушла из одного и появилась в другом.
 */
export function useIssueConvert(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: IssueConvert) =>
      apiRequest<IssueDetail>(`/api/issues/${issueKey}/convert`, { method: 'POST', body: input }),
    onSuccess: (issue) => {
      const before = queryClient.getQueryData<IssueDetail>(['issue', issueKey]);
      queryClient.setQueryData(['issue', issueKey], issue);
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['transitions', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['plan-view'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Уровень сменился: у подзадачи детей быть не может, у задачи — появился раздел детей.
      void queryClient.invalidateQueries({ queryKey: ['issue-children', issueKey] });
      invalidateParentChildren(queryClient, issue, before);
    },
  });
}

/**
 * Значения для формы создания. Ключ включает и тип: обязательные поля экрана создания
 * у каждого типа свои, и при смене типа список нужно перечитать.
 */
export function createOptionsQuery(projectId: string | null, issueTypeId: string | null) {
  return queryOptions({
    queryKey: ['create-options', projectId, issueTypeId],
    queryFn: () =>
      apiRequest<IssueCreateOptions>('/api/meta/create-options', {
        query: { projectId: projectId ?? '', issueTypeId: issueTypeId ?? undefined },
      }),
    enabled: projectId !== null,
    // Смена типа меняет ключ запроса: без прежних данных списки формы мигали бы на каждый выбор.
    placeholderData: (previous) => previous,
    staleTime: 5 * 60_000,
  });
}

/**
 * Создание задачи. Сервер возвращает карточку созданной, и она сразу кладётся в кэш:
 * открывающееся следом окно задачи не должно перезапрашивать то, что уже пришло.
 */
export function useIssueCreate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: IssueCreate) =>
      apiRequest<IssueDetail>('/api/issues', { method: 'POST', body: input }),
    onSuccess: (issue, input) => {
      queryClient.setQueryData(['issue', issue.key], issue);
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      // В разделах показан счётчик задач — он тоже изменился.
      void queryClient.invalidateQueries({ queryKey: ['projects'] });

      if (input.parentKey) {
        void queryClient.invalidateQueries({ queryKey: ['issue-children', input.parentKey] });
        // Карточку родителя тоже: правка в ней идёт с `updated`, а он мог сдвинуться.
        void queryClient.invalidateQueries({ queryKey: ['issue', input.parentKey] });
      }
    },
  });
}

/**
 * Переход статуса. Сбрасывается больше, чем таблица: строка плана красится по категории статуса
 * и по ней же уходит в конец списка, а счётчик раздела считает незавершённые — перевод задачи
 * в «Готово» меняет и то, и другое.
 */
export function useTransitionMutation(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: TransitionRequest) =>
      apiRequest<IssueDetail>(`/api/issues/${issueKey}/transitions`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (issue) => {
      queryClient.setQueryData(['issue', issueKey], issue);
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['transitions', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['plan-view'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      invalidateParentChildren(queryClient, issue);
    },
  });
}

/**
 * Список подзадач в карточке родителя показывает статус и название каждой — после правки
 * подзадачи он устарел. Из карточки подзадачи в родителя переходят по ссылке над названием,
 * и там должно быть видно только что сделанное.
 */
function invalidateParentChildren(
  queryClient: ReturnType<typeof useQueryClient>,
  issue: IssueDetail,
  before?: IssueDetail,
): void {
  const parents = new Set([issue.parent?.key, before?.parent?.key]);
  for (const key of parents) {
    if (key) void queryClient.invalidateQueries({ queryKey: ['issue-children', key] });
  }
}

/**
 * Удаление задачи. `withSubtasks` — согласие унести подзадачи: без него сервер отвечает
 * `ISSUE_HAS_SUBTASKS`, и спрашиваем второй раз, уже назвав их число.
 */
export function useIssueDelete(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options: { withSubtasks: boolean }) =>
      apiRequest<{ subtasks: number; removedFromPlans: number }>(`/api/issues/${issueKey}`, {
        method: 'DELETE',
        query: options.withSubtasks ? { withSubtasks: 'true' } : {},
      }),
    onSuccess: () => {
      // Задачи больше нет: её карточку из кэша убираем, а таблицу, разделы и планы перечитываем.
      queryClient.removeQueries({ queryKey: ['issue', issueKey] });
      void queryClient.invalidateQueries({ queryKey: ['issues'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['plan-view'] });
      // Удалённая могла быть чьей-то подзадачей: списки детей устаревают, но перечитываются,
      // только когда их снова откроют. Сразу — значило бы спросить у Jira детей задачи,
      // которой больше нет, пока её карточка ещё не закрылась.
      void queryClient.invalidateQueries({ queryKey: ['issue-children'], refetchType: 'none' });
    },
  });
}

export function useCommentMutation(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AdfDocument) =>
      apiRequest<Comment>(`/api/issues/${issueKey}/comments`, {
        method: 'POST',
        body: { body },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['comments', issueKey] });
    },
  });
}

/**
 * Прикрепление файла к задаче.
 *
 * Файл уезжает телом запроса, имя — параметром: складывать его в base64 внутрь JSON значило бы
 * раздуть каждую загрузку на треть. После успеха перечитывается карточка — вложения приходят
 * в ней же, и заодно обновляется `updated`, на котором держится оптимистичная блокировка правки.
 */
export function useAttachmentUpload(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) =>
      apiUpload<{ attachments: Attachment[] }>(
        `/api/issues/${issueKey}/attachments?filename=${encodeURIComponent(file.name)}` +
          `&type=${encodeURIComponent(file.type || 'application/octet-stream')}`,
        file,
        // Тело — просто байты: тип уехал параметром, чтобы разбор на сервере от него не зависел.
        'application/octet-stream',
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
    },
  });
}

/** Удаление вложения: файл уходит из задачи в Jira, поэтому карточку перечитываем целиком. */
export function useAttachmentDelete(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (attachmentId: string) =>
      apiRequest<{ ok: true }>(`/api/attachments/${attachmentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issueKey] });
    },
  });
}

// ── Планирование ─────────────────────────────────────────────────────────────

export interface PlanViewParams {
  owner: string;
  start: string;
  days: number;
  q?: string | undefined;
}

export function planViewQuery(params: PlanViewParams) {
  return queryOptions({
    queryKey: ['plan-view', params],
    queryFn: () =>
      apiRequest<PlanView>('/api/plans', {
        query: {
          owner: params.owner,
          start: params.start,
          days: params.days,
          q: params.q?.trim() || undefined,
        },
      }),
    staleTime: 5_000,
  });
}

/**
 * Ключи задач из своего плана на текущую неделю — по ним таблица подсвечивает строки.
 *
 * Неделя берётся всегда от сегодняшнего понедельника, а не та, что открыта в планировании:
 * подсветка отвечает на вопрос «взято ли это в работу сейчас», и листание недель в другом
 * разделе не должно менять смысл цвета в таблице. Плана может ещё не быть — тогда набор пуст.
 */
export function usePlanIssueKeys(): ReadonlySet<string> {
  const { data } = useQuery(planViewQuery({ owner: 'me', start: mondayOf(todayIso()), days: 7 }));

  return useMemo(
    () =>
      new Set((data?.plans ?? []).flatMap((plan) => plan.items.map((item) => item.issueKey))),
    [data],
  );
}

/**
 * Взять задачу в план текущей недели — то, что обещает подсказка «P — в план» под таблицей.
 *
 * Плана на неделю может ещё не быть: он заводится тем же первым нажатием, а не отдельным
 * действием. Обратную связь даёт сама таблица — строка добавленной задачи становится голубой.
 */
export function useAddIssueToPlanMutation() {
  const queryClient = useQueryClient();
  const start = mondayOf(todayIso());
  const view = useQuery(planViewQuery({ owner: 'me', start, days: 7 }));
  const planId = view.data?.plans[0]?.id ?? '';

  return useMutation({
    mutationFn: async (issueKey: string) => {
      const id =
        planId ||
        (
          await apiRequest<{ planId: string }>('/api/plans', {
            method: 'POST',
            body: { start, days: 7 },
          })
        ).planId;

      return apiRequest(`/api/plans/${id}/items`, {
        method: 'POST',
        body: { issueKeys: [issueKey] },
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['plan-view'] });
    },
  });
}

export const planOwnersQuery = queryOptions({
  queryKey: ['plan-owners'],
  queryFn: () => apiRequest<{ owners: UserRef[] }>('/api/plans/owners'),
  staleTime: 5 * 60_000,
});

// ── Настройки ────────────────────────────────────────────────────────────────

export interface SettingsResponse {
  settings: TenantSettings;
  fields: FieldCandidates;
  /** Код ошибки, если справочник полей получить не удалось: настройки всё равно показываем. */
  fieldsError: string | null;
  connection: { connectedAt: string | null; syncSourceName: string | null };
}

export const settingsQuery = queryOptions({
  queryKey: ['settings'],
  queryFn: () => apiRequest<SettingsResponse>('/api/settings'),
  staleTime: 30_000,
});

export const membersQuery = queryOptions({
  queryKey: ['members'],
  queryFn: () => apiRequest<{ members: Member[] }>('/api/members'),
  staleTime: 60_000,
});

export function fieldOptionsQuery(issueKey: string | null) {
  return queryOptions({
    queryKey: ['field-options', issueKey],
    queryFn: () => apiRequest<IssueFieldOptions>(`/api/issues/${issueKey ?? ''}/field-options`),
    enabled: issueKey !== null,
    // Состав типов и список назначаемых меняются редко.
    staleTime: 5 * 60_000,
  });
}

// ── Встречи Fathom ───────────────────────────────────────────────────────────

/**
 * Состояние подключения к Fathom. Ключ личный, поэтому запрос принадлежит пользователю,
 * а не тенанту: у коллеги в том же сайте Jira подключения может не быть вовсе.
 */
export const fathomConnectionQuery = queryOptions({
  queryKey: ['fathom-connection'],
  queryFn: () => apiRequest<FathomConnection>('/api/fathom/connection'),
  staleTime: 60_000,
});

/**
 * Встречи. Курсор приходит от Fathom и листается прокруткой; размер страницы задаёт он же —
 * параметра «сколько» у списка встреч нет.
 *
 * Запрос включается только с действующим ключом: без него ответ всегда одинаков, и незачем
 * ходить на сервер ради заранее известной ошибки. Повторов нет по той же причине.
 */
export function meetingsQuery(enabled: boolean, archived = false, projectId: string | null = null) {
  return infiniteQueryOptions({
    // Раздел входит в ключ: вкладка направления и общий список встреч — разные выдачи,
    // и страницы одной не должны подмешиваться в другую.
    queryKey: ['meetings', archived, projectId],
    queryFn: ({ pageParam, signal }) =>
      apiRequest<MeetingPage>('/api/meetings', {
        signal,
        query: {
          cursor: pageParam ?? undefined,
          archived: archived ? 'true' : undefined,
          projectId: projectId ?? undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/** Сохранение ключа. Сервер проверяет его обращением к Fathom, поэтому ответ уже достоверен. */
export function useFathomKeySave() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKey: string) =>
      apiRequest<FathomConnection>('/api/fathom/key', { method: 'PUT', body: { apiKey } }),
    onSuccess: (connection) => {
      queryClient.setQueryData(['fathom-connection'], connection);
      // Ключ сменился — прежние страницы принадлежали другому человеку.
      queryClient.removeQueries({ queryKey: ['meetings'] });
    },
  });
}

/**
 * Привязка встречи к разделу прямо в таблице.
 *
 * Обновление оптимистичное и без перечитывания списка: страницы встреч идут из Fathom по его
 * курсору, и запрос за ними ради одной изменившейся ячейки стоил бы обращения к чужому сервису
 * с его лимитом в 60 запросов в минуту. Не сохранилось — возвращаем прежнее значение.
 */
export function useMeetingSectionMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { meetingId: string; projectId: string | null }) =>
      apiRequest<{ projectId: string | null }>(`/api/meetings/${input.meetingId}/section`, {
        method: 'PUT',
        body: { projectId: input.projectId },
      }),
    onMutate: (input) =>
      patchMeetings(queryClient, (page, key) => ({
        ...page,
        items: page.items
          // Вкладка направления показывает встречи одного раздела: перепривязанной строке
          // в ней уже не место, и она уходит сразу, а не после перезапроса.
          .filter((meeting) => meeting.id !== input.meetingId || key.projectId === null)
          .map((meeting) =>
            meeting.id === input.meetingId ? { ...meeting, projectId: input.projectId } : meeting,
          ),
      })),
    onError: (_error, _input, context) => {
      restoreMeetings(queryClient, context);
    },
  });
}

/**
 * Название встречи. Правка остаётся в Jisy: у внешнего API Fathom нет ни одного метода записи,
 * поэтому в самой записи название прежнее — интерфейс так и подписывает переименованные строки.
 *
 * Обновление оптимистичное, как и у раздела: перечитывать страницы Fathom ради одной ячейки
 * дорого. Пустая строка снимает переопределение, и в списке возвращается название из Fathom.
 */
export function useMeetingTitleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { meetingId: string; title: string }) =>
      apiRequest<{ title: string }>(`/api/meetings/${input.meetingId}/title`, {
        method: 'PUT',
        body: { title: input.title },
      }),
    onMutate: (input) => {
      const title = input.title.trim();
      return patchMeetings(queryClient, (page) => ({
        ...page,
        items: page.items.map((meeting) =>
          meeting.id === input.meetingId
            ? {
                ...meeting,
                title: title === '' ? meeting.sourceTitle : title,
                renamed: title !== '',
              }
            : meeting,
        ),
      }));
    },
    onError: (_error, _input, context) => {
      restoreMeetings(queryClient, context);
    },
  });
}

/**
 * Архив встречи. Строка сразу пропадает из списка: основной список и архив — разные режимы,
 * и заархивированная встреча в первом уже не к месту. Перезапроса нет по той же причине,
 * что и у раздела, — страницы идут из Fathom.
 */
export function useMeetingArchiveMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { meetingId: string; archived: boolean }) =>
      apiRequest<{ archived: boolean }>(`/api/meetings/${input.meetingId}/archived`, {
        method: 'PUT',
        body: { archived: input.archived },
      }),
    onMutate: (input) =>
      patchMeetings(queryClient, (page) => ({
        ...page,
        items: page.items.filter((meeting) => meeting.id !== input.meetingId),
      })),
    onError: (_error, _input, context) => {
      restoreMeetings(queryClient, context);
    },
  });
}

/**
 * Правка кэша страниц встреч. Выдач несколько — основной список, архив и вкладка каждого
 * направления, — поэтому изменение идёт по всем сразу: строка не должна остаться прежней
 * в том режиме, который сейчас не открыт. Раздел выдачи передаётся правке: в списке
 * направления перепривязанной встрече не место, а в общем — место.
 */
function patchMeetings(
  queryClient: ReturnType<typeof useQueryClient>,
  patch: (page: MeetingPage, key: { projectId: string | null }) => MeetingPage,
): { previous: Array<[readonly unknown[], InfiniteData<MeetingPage> | undefined]> } {
  const previous = queryClient.getQueriesData<InfiniteData<MeetingPage>>({
    queryKey: ['meetings'],
  });

  for (const [key] of previous) {
    // Ключ запроса — ['meetings', archived, projectId]; раздела может не быть у старых ключей.
    const projectId = typeof key[2] === 'string' ? key[2] : null;

    queryClient.setQueryData<InfiniteData<MeetingPage>>(key, (data) =>
      data === undefined
        ? data
        : { ...data, pages: data.pages.map((page) => patch(page, { projectId })) },
    );
  }

  return { previous };
}

function restoreMeetings(
  queryClient: ReturnType<typeof useQueryClient>,
  context: { previous: Array<[readonly unknown[], InfiniteData<MeetingPage> | undefined]> } | undefined,
): void {
  for (const [key, data] of context?.previous ?? []) queryClient.setQueryData(key, data);
}

export function useFathomDisconnect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiRequest<FathomConnection>('/api/fathom/key', { method: 'DELETE' }),
    onSuccess: (connection) => {
      queryClient.setQueryData(['fathom-connection'], connection);
      queryClient.removeQueries({ queryKey: ['meetings'] });
    },
  });
}

// ── Ссылки направлений ───────────────────────────────────────────────────────

/**
 * Ссылки раздела. Список короткий и целиком помещается в один ответ: постраничности здесь нет,
 * как и у заметок, — направлений со ста ссылками не бывает.
 */
export function linksQuery(projectId: string | null) {
  return queryOptions({
    queryKey: ['links', projectId],
    queryFn: () => apiRequest<ProjectLinkList>(`/api/projects/${projectId ?? ''}/links`),
    enabled: projectId !== null,
    staleTime: 15_000,
  });
}

/**
 * Правка списка ссылок в кэше. Ответ сервера кладётся на место, а не перечитывается запросом:
 * порядок в списке — порядок добавления, и от переименования он не меняется.
 */
function patchLinks(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string | null,
  patch: (links: ProjectLink[]) => ProjectLink[],
): void {
  queryClient.setQueryData<ProjectLinkList>(['links', projectId], (data) =>
    data === undefined ? data : { links: patch(data.links) },
  );
}

export function useLinkCreate(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { url: string; title: string }) =>
      apiRequest<ProjectLink>(`/api/projects/${projectId ?? ''}/links`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (link) => {
      patchLinks(queryClient, projectId, (links) => [...links, link]);
    },
  });
}

export function useLinkSave(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; url?: string; title?: string }) =>
      apiRequest<ProjectLink>(`/api/links/${input.id}`, {
        method: 'PATCH',
        body: {
          ...(input.url === undefined ? {} : { url: input.url }),
          ...(input.title === undefined ? {} : { title: input.title }),
        },
      }),
    onSuccess: (link) => {
      patchLinks(queryClient, projectId, (links) =>
        links.map((item) => (item.id === link.id ? link : item)),
      );
    },
  });
}

export function useLinkDelete(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (linkId: string) =>
      apiRequest<{ ok: true }>(`/api/links/${linkId}`, { method: 'DELETE' }),
    onSuccess: (_result, linkId) => {
      patchLinks(queryClient, projectId, (links) => links.filter((item) => item.id !== linkId));
    },
  });
}

// ── Заметки направлений ──────────────────────────────────────────────────────

/** Список заметок раздела: без тел, поэтому переключение между разделами дешёвое. */
export function notesQuery(projectId: string | null) {
  return queryOptions({
    queryKey: ['notes', projectId],
    queryFn: () => apiRequest<NoteList>(`/api/projects/${projectId ?? ''}/notes`),
    enabled: projectId !== null,
    staleTime: 15_000,
  });
}

export function noteQuery(noteId: string | null) {
  return queryOptions({
    queryKey: ['note', noteId],
    queryFn: () => apiRequest<Note>(`/api/notes/${noteId ?? ''}`),
    /*
     * Единственный запрос, который не перечитывается при возврате на вкладку: тело заметки
     * лежит в редакторе, и ответ сервера заменил бы содержимое прямо под курсором вместе
     * с ещё не сохранённым абзацем.
     */
    refetchOnWindowFocus: false,
    enabled: noteId !== null,
    // Открытая заметка правится этим же экраном: перечитывать её с сервера незачем.
    staleTime: 60_000,
  });
}

export function useNoteCreate(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { title: string }) =>
      apiRequest<Note>(`/api/projects/${projectId ?? ''}/notes`, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (note) => {
      queryClient.setQueryData(['note', note.id], note);
      void queryClient.invalidateQueries({ queryKey: ['notes', projectId] });
    },
  });
}

/**
 * Сохранение заметки. Ответ кладётся в кэш заметки, но список перечитывается запросом:
 * в нём стоит и порядок по времени правки, и подпись «кто менял последним».
 */
export function useNoteSave(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; title?: string; body?: NoteBody }) =>
      apiRequest<Note>(`/api/notes/${input.id}`, {
        method: 'PATCH',
        body: {
          ...(input.title === undefined ? {} : { title: input.title }),
          ...(input.body === undefined ? {} : { body: input.body }),
        },
      }),
    onSuccess: (note) => {
      queryClient.setQueryData(['note', note.id], note);
      void queryClient.invalidateQueries({ queryKey: ['notes', projectId] });
    },
  });
}

export function useNoteDelete(projectId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (noteId: string) =>
      apiRequest<{ ok: true }>(`/api/notes/${noteId}`, { method: 'DELETE' }),
    onSuccess: (_result, noteId) => {
      queryClient.removeQueries({ queryKey: ['note', noteId] });
      void queryClient.invalidateQueries({ queryKey: ['notes', projectId] });
    },
  });
}

/** Картинка из буфера обмена: уходит файлом, возвращается адресом для узла картинки. */
export function uploadNoteImage(noteId: string, blob: Blob): Promise<NoteImage> {
  return apiUpload<NoteImage>(`/api/notes/${noteId}/images`, blob);
}

// ── Пространства ─────────────────────────────────────────────────────────────

/**
 * Подключённые пространства. Список приходит из базы и не зависит от Atlassian, поэтому
 * переключатель открывается мгновенно даже тогда, когда Jira недоступна.
 */
export const workspacesQuery = queryOptions({
  queryKey: ['workspaces'],
  queryFn: () => apiRequest<WorkspaceList>('/api/workspaces'),
  staleTime: 60_000,
});

/**
 * Сайты, которые выданный токен уже открывает, но в Jisy их ещё нет. Запрашиваются только
 * при открытом переключателе: за ними идёт запрос в Atlassian.
 */
export function availableSitesQuery(enabled: boolean) {
  return queryOptions({
    queryKey: ['workspaces', 'available'],
    queryFn: () => apiRequest<AvailableSites>('/api/workspaces/available'),
    enabled,
    staleTime: 60_000,
    // Недоступная Atlassian не должна превращать открытие списка в очередь повторов.
    retry: false,
  });
}

/**
 * Переключение и подключение перезагружают страницу целиком.
 *
 * Сервер меняет cookie сессии, и в новом пространстве другими становятся все данные до единой:
 * разделы, задачи, планы, настройки. Точечная чистка кэша здесь означала бы помнить обо всех
 * запросах приложения — перезагрузка делает то же самое и не забывает ничего.
 */
function reloadIntoWorkspace(): void {
  window.location.assign('/');
}

export function useWorkspaceSwitch() {
  return useMutation({
    mutationFn: (tenantId: string) =>
      apiRequest<{ tenantId: string }>(`/api/workspaces/${tenantId}/switch`, { method: 'POST' }),
    onSuccess: reloadIntoWorkspace,
  });
}

export function useWorkspaceConnect() {
  return useMutation({
    mutationFn: (cloudId: string) =>
      apiRequest<{ tenantId: string }>('/api/workspaces', { method: 'POST', body: { cloudId } }),
    onSuccess: reloadIntoWorkspace,
  });
}

/**
 * Отключение. Уходило ли текущее пространство, видно по ответу: сервер отвечает, куда переехала
 * сессия, а если переезжать некуда — гасит её, и остаётся только экран подключения.
 */
export function useWorkspaceDisconnect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) =>
      apiRequest<WorkspaceDisconnectResult>(`/api/workspaces/${tenantId}`, { method: 'DELETE' }),
    onSuccess: (result, tenantId) => {
      const wasCurrent = queryClient
        .getQueryData<WorkspaceList>(['workspaces'])
        ?.workspaces.find((workspace) => workspace.tenantId === tenantId)?.current;

      if (!wasCurrent) {
        void queryClient.invalidateQueries({ queryKey: ['workspaces'] });
        return;
      }

      if (result.switchedTo) {
        reloadIntoWorkspace();
        return;
      }

      queryClient.clear();
      window.location.assign('/connect');
    },
  });
}
